import type { WaysOutcomePick } from "../engine/ways";
import { GridPreview } from "./GridPreview";

/**
 * Spec 021 US3 (T027 / FR-016) — ways-win affordance.
 *
 * A ways game (`evaluation.type: "ways"`) has no paylines to overlay, so
 * the replay's line view doesn't apply. This panel renders one sampled
 * spin's ways wins legibly: the grid it paid on, and — per win — the
 * **leading symbol**, the **run length** (`K of N` reels), the **ways
 * count** (`W = c₀ × … × c_{K-1}`, the number of matching paths), and the
 * payout. The cells on each win's paying prefix are highlighted so the
 * left-anchored run is visible.
 *
 * Shown only when the loaded game is a ways game *and* a sampled spin
 * produced a ways win (`pickWaysOutcome`), so a lines game — which has no
 * ways info — never renders it. No `MetricsResult` consumer change (R7):
 * the wins are reconstructed from the spun grid, not a new metrics field.
 */
export function WaysPanel({ pick }: { pick: WaysOutcomePick }) {
  const { grid, wins, index } = pick;
  const reelCount = grid.length;
  const leading = wins[0];

  // Cells the leading win actually matched over its paying prefix — the
  // symbol's own cells plus substituting wilds, not the whole reel column.
  // Highlighting only the leading win keeps the grid legible when several
  // symbols pay at once.
  const highlighted = new Set<string>();
  for (const [reel, row] of leading?.positions ?? []) {
    highlighted.add(`${reel}-${row}`);
  }

  return (
    <section
      data-testid="ways-panel"
      className="border border-paper-border p-4 dark:border-term-border"
    >
      <header className="mb-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-paper-bright dark:text-term-bright">
          Ways win
        </h2>
        <p className="text-[10px] text-paper-hint dark:text-term-hint">
          spin #{index.toLocaleString()} of the sample pass (same seed) ·
          adjacent-reel evaluation, no paylines
        </p>
      </header>

      <GridPreview
        grid={grid}
        testId="ways-grid"
        renderCell={(reel, row, symbol) => {
          const lit = highlighted.has(`${reel}-${row}`);
          return (
            <div
              key={`${reel}-${row}`}
              data-testid={`ways-cell-${reel}-${row}`}
              data-lit={lit ? "true" : undefined}
              className={
                "flex h-12 w-full items-center justify-center border text-xs font-bold " +
                (lit
                  ? "border-paper-accent bg-paper-accent/10 text-paper-bright dark:border-term-accent dark:bg-term-accent/10 dark:text-term-bright"
                  : "border-paper-border text-paper-text dark:border-term-border dark:text-term-text")
              }
            >
              <span className="truncate px-1">{symbol}</span>
            </div>
          );
        }}
      />

      <table
        className="mt-3 w-full text-left text-xs"
        data-testid="ways-win-table"
      >
        <thead>
          <tr className="text-paper-hint dark:text-term-hint">
            <th className="py-1 pr-2 font-semibold">Symbol</th>
            <th className="py-1 pr-2 font-semibold">Run</th>
            <th className="py-1 pr-2 font-semibold">Ways</th>
            <th className="py-1 pr-2 font-semibold text-right">Pays</th>
          </tr>
        </thead>
        <tbody>
          {wins.map((win, i) => (
            <tr
              key={`${win.symbol}-${i}`}
              data-testid={`ways-win-row-${i}`}
              className="border-t border-paper-border/50 dark:border-term-border/50"
            >
              <td className="py-1 pr-2 font-bold text-paper-text dark:text-term-text">
                {win.symbol}
                {win.isWild ? (
                  <span className="ml-1 text-[9px] uppercase text-paper-hint dark:text-term-hint">
                    wild
                  </span>
                ) : null}
              </td>
              <td className="py-1 pr-2 text-paper-text dark:text-term-text">
                {win.runLength} of {reelCount}
              </td>
              <td className="py-1 pr-2 text-paper-text dark:text-term-text">
                {win.waysCount.toLocaleString()}
                <span className="ml-1 text-[9px] text-paper-hint dark:text-term-hint">
                  {win.perReelCounts.join("×")}
                </span>
              </td>
              <td className="py-1 pr-2 text-right font-mono text-paper-bright dark:text-term-bright">
                {win.payout.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                ×
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
